import { useState, useEffect, ReactNode } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

interface ShowMoreWrapperProps {
  children: ReactNode;
  maxHeight?: string;
  gradientFrom?: string;
  gradientTo?: string;
  gradientToDark?: string;
  buttonClassName?: string;
  defaultExpanded?: boolean;
  showMoreText?: string;
  showLessText?: string;
}

export function ShowMoreWrapper({
  children,
  maxHeight = "3rem",
  gradientFrom = "transparent",
  gradientTo = "rgb(31 41 55)", // bg-gray-800
  gradientToDark,
  buttonClassName = "",
  defaultExpanded = false,
  showMoreText = "Show more",
  showLessText = "Show less",
}: ShowMoreWrapperProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains("dark"));
    };
    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const effectiveGradientTo = isDark && gradientToDark ? gradientToDark : gradientTo;

  return (
    <div>
      <div className="relative">
        <div
          className={`overflow-hidden transition-all duration-300 ${
            isExpanded ? "max-h-full" : ""
          }`}
          style={{
            maxHeight: isExpanded ? "none" : maxHeight,
          }}
        >
          {children}
        </div>

        {!isExpanded && (
          <div
            className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, ${gradientFrom}, ${effectiveGradientTo})`,
            }}
          />
        )}
      </div>

      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`mt-1 ml-auto flex items-center gap-1 text-xs text-gray-400 hover:text-gray-300 transition-colors bg-transparent border-none cursor-pointer ${buttonClassName}`}
      >
        {isExpanded ? (
          <>
            {showLessText}
            <ChevronUpIcon className="h-3 w-3" />
          </>
        ) : (
          <>
            {showMoreText}
            <ChevronDownIcon className="h-3 w-3" />
          </>
        )}
      </button>
    </div>
  );
}
